import { useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Close } from "@mui/icons-material";
import { Popover } from "@mui/material";
import { TbDownload, TbPlus } from "react-icons/tb";
import { FilterDialog } from "../Filters/FilterDialog";
import { useToast } from "../../../Hooks/toast";

export const GridFilterCheckboxOption = ({ id, label, checked, onChange }) => (
  <label
    htmlFor={id}
    className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-brand-dark-elevated"
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 rounded border-gray-300 text-brand-gold-mid focus:ring-brand-gold-mid/30"
    />
    <span>{label}</span>
  </label>
);

const PopoverActions = ({ onCancel, onApply }) => (
  <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-3 dark:border-brand-dark-border">
    <button type="button" className="grid-history-action-btn" onClick={onCancel}>
      {intl.get("common.cancel")}
    </button>
    <button type="button" className="grid-history-action-btn grid-history-action-btn--primary" onClick={onApply}>
      {intl.get("common.apply")}
    </button>
  </div>
);

export const GridFilterBar = ({
  chips,
  filters,
  emptyFilters,
  onFiltersChange,
  filteredItems,
  onExport,
  onExportClick,
  buildFilterTags,
  hasActiveFilters,
  removeFilterTag,
  isChipFilterActive,
  renderFilterForm,
  popoverFieldMap = {},
  moreFiltersChipId = "more",
  actionsStart = null,
}) => {
  const toast = useToast();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeChip, setActiveChip] = useState(null);
  const [draft, setDraft] = useState(emptyFilters);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  const appliedTags = useMemo(() => buildFilterTags(filters), [buildFilterTags, filters]);
  const filtersActive = hasActiveFilters(filters);

  const openPopover = (event, chipId) => {
    if (chipId === moreFiltersChipId) {
      setDraft({ ...filters });
      setMoreFiltersOpen(true);
      return;
    }

    setDraft({ ...filters });
    setActiveChip(chipId);
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
    setActiveChip(null);
  };

  const applyDraft = () => {
    onFiltersChange(draft);
    closePopover();
    setMoreFiltersOpen(false);
  };

  const handleClearFilters = () => {
    onFiltersChange(emptyFilters);
    setDraft(emptyFilters);
    closePopover();
    setMoreFiltersOpen(false);
  };

  const handleRemoveTag = (tagKey) => {
    onFiltersChange(removeFilterTag(filters, tagKey));
  };

  const handleExport = () => {
    if (onExportClick) {
      onExportClick();
      return;
    }

    if (filteredItems.length === 0) {
      toast.addToast({
        type: "info",
        title: intl.get("common.nothingToExport"),
        description: intl.get("common.noRecordsFiltered"),
      });
      return;
    }

    onExport(filteredItems);
    toast.addToast({
      type: "success",
      title: intl.get("common.exportComplete"),
      description: intl.get("common.exportCompleteCsv", { count: filteredItems.length }),
    });
  };

  const activeChipLabel = chips.find((chip) => chip.id === activeChip)?.label;
  const activeFieldProps = activeChip ? popoverFieldMap[activeChip] : null;

  return (
    <div className="grid-history-filter-section">
      <div className="grid-history-filter-bar">
        <div className="grid-history-filter-bar__chips">
          {chips.map((chip) => {
            const isActive = chip.id === moreFiltersChipId
              ? filtersActive
              : isChipFilterActive(filters, chip.id);

            return (
              <button
                key={chip.id}
                type="button"
                className={`grid-filter-chip ${isActive ? "grid-filter-chip--active" : ""}`}
                onClick={(event) => openPopover(event, chip.id)}
              >
                <TbPlus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {chip.label}
              </button>
            );
          })}

          {(filtersActive || appliedTags.length > 0) && (
            <>
              {appliedTags.map((tag) => (
                <button
                  key={tag.key}
                  type="button"
                  className="btn-filter-tag"
                  onClick={() => handleRemoveTag(tag.key)}
                >
                  <span className="truncate">
                    {tag.label}: <strong>{tag.value}</strong>
                  </span>
                  <Close sx={{ fontSize: 14, flexShrink: 0 }} />
                </button>
              ))}

              <button type="button" className="grid-history-action-btn" onClick={handleClearFilters}>
                {intl.get("common.clearFilters")}
              </button>
            </>
          )}
        </div>

        <div className="grid-history-filter-bar__actions">
          {actionsStart}
          <button type="button" className="grid-history-action-btn" onClick={handleExport}>
            <TbDownload className="h-4 w-4" aria-hidden />
            {intl.get("common.export")}
          </button>
        </div>
      </div>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          className: "app-card",
          sx: { mt: 1, width: { xs: "calc(100vw - 2rem)", sm: 360 }, maxWidth: "calc(100vw - 2rem)", p: 2 },
        }}
      >
        {activeChip && activeFieldProps ? (
          <>
            <p className="mb-3 text-sm font-semibold text-brand-dark dark:text-gray-100">
              {activeChipLabel}
            </p>
            {renderFilterForm(draft, setDraft, activeFieldProps)}
            <PopoverActions onCancel={closePopover} onApply={applyDraft} />
          </>
        ) : null}
      </Popover>

      <FilterDialog
        open={moreFiltersOpen}
        onClose={() => setMoreFiltersOpen(false)}
        footer={(
          <>
            <button type="button" className="grid-history-action-btn" onClick={handleClearFilters}>
              {intl.get("words.clear")}
            </button>
            <button type="button" className="grid-history-action-btn grid-history-action-btn--primary" onClick={applyDraft}>
              {intl.get("common.filter")}
            </button>
          </>
        )}
      >
        {renderFilterForm(draft, setDraft, {})}
      </FilterDialog>
    </div>
  );
};
